import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  ExternalLink,
  Mail,
  MailX,
  MoreHorizontal,
  Palette,
  RotateCcw,
  XCircle,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@eduviaos/ui-kit";
import { type ApiError } from "@eduviaos/shared";
import { api } from "@/lib/api";
import { OpenCentreAdminDialog } from "@/components/OpenCentreAdminDialog";

export interface PendingInvite {
  invite_id: string;
  admin_email: string;
  admin_full_name: string;
  expires_at: string;
  last_email_sent_at: string | null;
  email_send_count: number;
  last_email_error: string | null;
}

interface PendingInviteListResponse {
  items: PendingInvite[];
  total: number;
}

interface ResendResponse {
  invite: {
    token: string;
    accept_url: string;
    expires_at: string;
    email_sent: boolean;
    email_error: string | null;
  };
}

interface Props {
  tenantSlug: string;
  /** Display name for the "Open centre admin" dialog title. */
  tenantName: string;
  /** Called when the SA clicks Resend AND the BE returns successfully. The
   * page-level component re-uses the existing one-shot success Sheet to
   * display the new accept URL. */
  onResendSuccess: (input: {
    slug: string;
    admin_email: string;
    accept_url: string;
    expires_at: string;
    token: string;
    email_sent: boolean;
    email_error: string | null;
  }) => void;
}

/**
 * Per-tenant action menu — Resend / Revoke pending invite, lazy-loads the
 * list-pending endpoint when the dropdown opens. Renders a "Pending invite"
 * micro-affordance on the row whenever there's at least one PENDING invite,
 * even when the menu is closed (so the SA can spot tenants that need
 * follow-up at a glance).
 */
export function TenantActionsCell({ tenantSlug, tenantName, onResendSuccess }: Props) {
  const queryClient = useQueryClient();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [supportOpen, setSupportOpen] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);

  // Lazy: only fetch when the menu is opened the first time. After that,
  // TanStack keeps the list fresh on every menu open via the standard
  // `staleTime` config.
  const pendingQuery = useQuery({
    queryKey: ["super-admin", "pending-invites", tenantSlug],
    queryFn: () => api.get<PendingInviteListResponse>(`/super-admin/tenants/${tenantSlug}/invites`),
    enabled: menuOpen,
  });

  const pendingInvite = pendingQuery.data?.items[0] ?? null;
  const hasPending = (pendingQuery.data?.total ?? 0) > 0;

  const resendMutation = useMutation<ResendResponse, ApiError, string>({
    mutationFn: (inviteId) =>
      api.post<ResendResponse>(`/super-admin/tenants/invites/${inviteId}/resend`),
    onSuccess: (data) => {
      setActionError(null);
      queryClient.invalidateQueries({
        queryKey: ["super-admin", "pending-invites", tenantSlug],
      });
      onResendSuccess({
        slug: tenantSlug,
        admin_email: pendingInvite?.admin_email ?? "",
        accept_url: data.invite.accept_url,
        expires_at: data.invite.expires_at,
        token: data.invite.token,
        email_sent: data.invite.email_sent,
        email_error: data.invite.email_error,
      });
      setMenuOpen(false);
    },
    onError: (err) => {
      setActionError(translateActionError(err, "Could not resend invite."));
    },
  });

  const revokeMutation = useMutation<void, ApiError, string>({
    mutationFn: async (inviteId) => {
      await api.post(`/super-admin/tenants/invites/${inviteId}/revoke`);
    },
    onSuccess: () => {
      setActionError(null);
      queryClient.invalidateQueries({
        queryKey: ["super-admin", "pending-invites", tenantSlug],
      });
    },
    onError: (err) => {
      setActionError(translateActionError(err, "Could not revoke invite."));
    },
  });

  return (
    <div className="flex items-center justify-end gap-2">
      {hasPending && (
        <Badge
          variant="warn"
          className="hidden whitespace-nowrap sm:inline-flex"
          title="One or more invites are pending acceptance"
        >
          <Mail className="size-3" aria-hidden="true" />
          Pending invite
        </Badge>
      )}
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label={`Actions for ${tenantSlug}`}>
            <MoreHorizontal className="size-4" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel className="flex items-center gap-2">
            <span className="truncate">{tenantSlug}</span>
            {pendingQuery.isFetching && (
              <Loader2 className="text-muted-foreground size-3 animate-spin" aria-hidden="true" />
            )}
            <DropdownMenuItem asChild>
              <Link to={`/tenants/${tenantSlug}/branding`}>
                <Palette className="size-4" aria-hidden="true" />
                Branding (logo, favicon, colours)
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </DropdownMenuLabel>
          <DropdownMenuItem
            onSelect={() => {
              setMenuOpen(false);
              setSupportOpen(true);
            }}
          >
            <ExternalLink className="size-4" aria-hidden="true" />
            Open centre admin
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {pendingQuery.isLoading ? (
            <DropdownMenuItem disabled>
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              Loading invites…
            </DropdownMenuItem>
          ) : pendingQuery.isError ? (
            <DropdownMenuItem disabled>
              <XCircle className="text-destructive size-4" aria-hidden="true" />
              Couldn&apos;t load invites
            </DropdownMenuItem>
          ) : pendingInvite ? (
            <>
              <div className="text-muted-foreground px-2 py-1.5 text-xs">
                <p className="text-foreground truncate font-medium">
                  {pendingInvite.admin_full_name}
                </p>
                <p className="truncate">{pendingInvite.admin_email}</p>
                <p className="mt-1 flex items-center gap-1.5 text-[11px]">
                  {pendingInvite.last_email_sent_at ? (
                    <>
                      <CheckCircle2 className="size-3 text-emerald-600" aria-hidden="true" />
                      <span>
                        Email sent{" "}
                        {pendingInvite.email_send_count > 1
                          ? `${pendingInvite.email_send_count}x`
                          : ""}
                      </span>
                    </>
                  ) : (
                    <>
                      <MailX className="size-3 text-amber-600" aria-hidden="true" />
                      <span>Email not delivered yet</span>
                    </>
                  )}
                </p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  resendMutation.mutate(pendingInvite.invite_id);
                }}
                disabled={resendMutation.isPending}
              >
                <RotateCcw className="size-4" aria-hidden="true" />
                {resendMutation.isPending ? "Resending…" : "Resend invite email"}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  revokeMutation.mutate(pendingInvite.invite_id);
                }}
                disabled={revokeMutation.isPending}
                className="text-destructive focus:text-destructive"
              >
                <XCircle className="size-4" aria-hidden="true" />
                {revokeMutation.isPending ? "Revoking…" : "Revoke invite"}
              </DropdownMenuItem>
              {actionError && (
                <div role="alert" className="text-destructive px-2 py-1.5 text-[11px]">
                  {actionError}
                </div>
              )}
            </>
          ) : (
            <DropdownMenuItem disabled>No pending invites</DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <OpenCentreAdminDialog
        open={supportOpen}
        onOpenChange={setSupportOpen}
        tenantSlug={tenantSlug}
        tenantName={tenantName}
      />
    </div>
  );
}

function translateActionError(err: ApiError, fallback: string): string {
  switch (err.code) {
    case "INVITE_ALREADY_USED":
      return "That invite is already accepted or revoked.";
    case "NOT_FOUND":
      return "Invite no longer exists.";
    case "FORBIDDEN":
      return "Your role can't perform this action.";
    case "UNAUTHORIZED":
      return "Your session expired. Sign in again.";
    default:
      return err.message || fallback;
  }
}
